import { skipToken, type QueryFunction, type QueryKey, type SkipToken } from '@tanstack/react-query';

/** Canvas tools that bring a new canvas into being. */
export const CANVAS_CREATE_TOOLS: ReadonlySet<string> = new Set(['create_canvas', 'import_doc_to_canvas']);

/** Title a Pending Canvas starts with, and falls back to when saved with a blank title. */
export const UNTITLED_DOCUMENT_TITLE = 'Untitled Document';

/** Name a chat has until its first answer or the user names it. */
export const DEFAULT_CHAT_TOPIC = 'New chat';

/** Return whether a chat, by its topic, has yet to be named. */
export const isChatUnnamed = (topic: string | null) => !topic || topic === DEFAULT_CHAT_TOPIC;

/** Return the name a document's title gives a chat still unnamed, or null for a title the user did not choose. */
export function chatNameFromDocumentTitle(title: string): string | null {
  const trimmed = title.trim();
  return trimmed && trimmed !== UNTITLED_DOCUMENT_TITLE ? trimmed : null;
}

/** Why a document action is unavailable during a Turn. */
export const RESPONDING_REASON = 'Available once the response finishes';

/** Return a version's save time as the version history and banner show it. */
export const formatVersionTime = (createdAt: string) =>
  new Date(createdAt)
    .toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short', hour12: false })
    .replace(',', '');

/** Query key for the list of canvases in a chat session. */
export const editorCanvasesQueryKey = (sessionId: string | undefined) => ['editor-canvases', sessionId] as const;

// An idle query never fetches or holds data, so every one of them can share a key.
const IDLE_QUERY_KEY = ['idle'] as const;

/** Options for a query that fetches. */
export type FetchingQuery<T> = { queryKey: QueryKey; queryFn: QueryFunction<T> };

/** Return `query`, or when it is null, options for a query that fetches nothing. */
export const queryOrIdle = <T>(
  query: FetchingQuery<T> | null
): { queryKey: QueryKey; queryFn: QueryFunction<T> | SkipToken } =>
  query ?? { queryKey: IDLE_QUERY_KEY, queryFn: skipToken };

const EDITOR_CANVAS_VERSIONS = 'editor-canvas-versions';

const canvasQueryKey =
  <F extends string>(family: F) =>
  (sessionId: string, canvasId: string) =>
    [family, sessionId, canvasId] as const;

/** Query key for one canvas, with its content. */
export const editorCanvasQueryKey = canvasQueryKey('editor-canvas');

/** Query key for one canvas's version history. */
export const editorCanvasVersionsQueryKey = canvasQueryKey(EDITOR_CANVAS_VERSIONS);

/** Query key prefix matching the version history of every canvas in a session. */
export const editorSessionCanvasVersionsQueryKey = (sessionId: string) => [EDITOR_CANVAS_VERSIONS, sessionId] as const;

/** Query key for the Document Hub exports of one canvas. */
export const editorCanvasHubExportsQueryKey = canvasQueryKey('editor-canvas-hub-exports');

/** Query key for the diff between two versions of one canvas. */
export const editorCanvasDiffQueryKey = (sessionId: string, canvasId: string, from: number, to: number) =>
  ['editor-canvas-diff', sessionId, canvasId, from, to] as const;

/** Canvas tools that change the content of an existing canvas, named by their `canvas_id` argument. */
export const CANVAS_EDIT_TOOLS: ReadonlySet<string> = new Set([
  'edit_canvas',
  'rewrite_canvas',
  'insert_canvas_images'
]);

/**
 * Canvas tools that bump the version (open_canvas is a read, so excluded).
 * Used to compute a card's before→after diff pair from its turn's edits.
 */
export const MUTATING_CANVAS_TOOLS: ReadonlySet<string> = new Set([...CANVAS_CREATE_TOOLS, ...CANVAS_EDIT_TOOLS]);

/** Tool names that carry canvas metadata in SSE tool_call_end events. */
export const CANVAS_TOOL_NAMES: ReadonlySet<string> = new Set([...MUTATING_CANVAS_TOOLS, 'open_canvas']);

/** Tool name for the pydantic-ai-harness Planning capability's plan-writing tool. */
export const PLAN_TOOL_NAME = 'write_plan';
